// shared signin code & config
import { UserManager } from 'oidc-client';

const debounceTime = 5000; // in miliseconds. = 5 seconds

// Global variables for ZAIUS coordination
let zaiusLoadPromise = null;
let zaiusInitialized = false;


function getConfig(location) {
  // set URL's if domain is test or local
  const hostname = location.hostname;
  const addTest = getIdDomainPrefix()
  const authUrl = `https://id${addTest}.southaustralia.com/`;
  // const authUrl = `https://localhost:5000/`;
  // const apiHubUrl = `https://apihub${addTest}.southaustralia.com/`;

  // OIDC config
  const config = {
    authority: authUrl, // Account/Login
    client_id: 'tripPlannerM',
    scope: 'openid profile email api1.read api1.write offline_access',
    response_type: 'code',
    redirect_uri: `${location.protocol}//${hostname}${location.port ? `:${location.port}` : ''}/signin-oidc`, //products/barossa/accommodation/cottage-in-the-vines
    post_logout_redirect_uri: `${location.protocol}//${hostname}${location.port ? `:${location.port}` : ''}/signout-oidc`,
    silent_redirect_uri: `${location.protocol}//${hostname}${location.port ? `:${location.port}` : ''}/silent-renew-oidc`,
    automaticSilentRenew: true,
    filterProtocolClaims: true,
    loadUserInfo: true,
    monitorSession: false,
    revokeAccessTokenOnSignout: true
  };

  return config;
}

export function getIdDomainPrefix() {
  // get ID server URL for iframe src attribute
  const hostname = window.location.host;

  switch (true) {
    case hostname.startsWith('web.'):
      return '2';
    case hostname.includes('test') || hostname.startsWith('dev.') || hostname.startsWith('localhost') || hostname.includes('predeploy'):
      return '-test';
  }

  return '';
}

export function getApiDomainPrefix() {
  // get ID server URL for iframe src attribute
  const hostname = window.location.host;

  switch (true) {
    case hostname.startsWith('web.'):
      return '2';
    case hostname.includes('predeploy-tm'):
      return '-predeploy';
    case hostname.includes('test') || hostname.startsWith('dev.') || hostname.startsWith('localhost') || hostname.includes('predeploy'):
      return 'hub-test';
  }

  return '';
}

export function getTripPlannerDomainPrefix() {
  // get ID server URL for iframe src attribute
  const hostname = window.location.host;

  switch (true) {
    case hostname.startsWith('web.'):
      return '2';
    case hostname.includes('predeploy-tm'):
      return '-webtest';
    case hostname.includes('test') || hostname.startsWith('dev.') || hostname.startsWith('localhost') || hostname.includes('predeploy'):
      return '-test';
  }

  return '';
}

function executeZaiusActions(user) {
  try {
    // Check if ZAIUS has already been initialized globally
    if (zaiusInitialized) {
      console.log("✅ ZAIUS already initialized, skipping initialization");

      // Still send events for this user
      doZaiusSendLogin(user);

      return;
    }

    // Initialize ZAIUS if not already initialized and window.zaius exists
    if (!zaiusInitialized && window.zaius) {

      // environment flag
      const hostname = window.location.hostname;
      const isTestEnv =
        hostname.includes("test") ||
        hostname.startsWith("dev") ||
        hostname.startsWith("local") ||
        hostname.includes("predeploy");

      if (isTestEnv) {
        localStorage.setItem("executeZaiusActions - Using Test", true);
        window.zaius.initialize('lKIe3kN5LlPetJ29HYbXVg-au1');
      } else {
        localStorage.setItem("executeZaiusActions - Using Prod", true);
        window.zaius.initialize('wbJmydsdMnYBbOTXjub8BA-au1');
      }
      localStorage.setItem("✅ Zaius initialized", "zaius");
      zaiusInitialized = true; // Mark as initialized globally
    }

    // Send login event
    doZaiusSendLogin(user);

  } catch (e) {
    console.error("🔥 Error occurred during zaius init or redirect", e);
  }
}

function doZaiusSendLogin(user) {
  // Will send login event if conditions are met.
  // Zaius is part of Optimizely.
  // Optimizely sometimes needs to know if the user is logged in or not.

  if (window.zaius) {
    window.zaius.event("User", {
      action: "login",
    });

    (async () => {
      // Skip if there is no user and profile and email.
      if (!user?.profile?.email) return;

      const hashEmail = await getHashEmailToODP(user.profile.email);
      const hcSatcId = getPRID();

      window.zaius.customer({
        email: user.profile.email,
        first_name: user.profile.given_name,
        last_name: user.profile.family_name,
        southaustralia_com_account: true,
        southaustralia_com_account_last_login: new Date().toISOString(),
      },
        {
          sha256_email_hash: `${hashEmail}`,
          hc_satc_id: hcSatcId,
          subscribed: user.profile?.middle_name?.toLowerCase() === "y" ? true : false
        });
    })();
  }
}

async function getHashEmailToODP(email) {
  try {
    const hashEmail = await hash(email);

    // Store result in localStorage for debugging or logging
    localStorage.setItem('getHashEmailToODP', `success ${hashEmail}`);

    // Return the hash object as requested
    const ee = { sha256_email_hash: hashEmail };
    return hashEmail;
  } catch (e) {
    localStorage.setItem('getHashEmailToODP', "failed " + e.message);
    console.error("Error in getHashEmailToODP:", e);
    return null; // Return null or handle the error as needed
  }
}

async function hash(string) {
  const utf8 = new TextEncoder().encode(string);
  return crypto.subtle.digest('SHA-256', utf8).then((hashBuffer) => {
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((bytes) => bytes.toString(16).padStart(2, '0'))
      .join('');
    return hashHex;
  });
}

async function ensureZaiusLoaded() {
  // If ZAIUS is already loaded and ready, return immediately
  if (window.zaius && typeof window.zaius !== "undefined" && typeof window.zaius.push === "function") {
    return Promise.resolve();
  }

  // If there's already a loading promise, return it
  if (zaiusLoadPromise) {
    return zaiusLoadPromise;
  }

  // Check if script is already in the DOM (might be loading from marketing scripts)
  const existingScript = document.querySelector('script[src*="zaius-min.js"]');
  if (existingScript) {
    // Script exists, wait for it to load
    zaiusLoadPromise = new Promise((resolve, reject) => {
      if (window.zaius && typeof window.zaius.push === "function") {
        resolve();
      } else {
        existingScript.onload = () => resolve();
        existingScript.onerror = () => reject(new Error("Failed to load existing Zaius script"));

        // Fallback timeout in case the script is already loaded but events didn't fire
        setTimeout(() => {
          if (window.zaius && typeof window.zaius.push === "function") {
            resolve();
          } else {
            reject(new Error("Timeout waiting for existing Zaius script"));
          }
        }, 5000);
      }
    });
    return zaiusLoadPromise;
  }

  // No existing script, create and load our own
  zaiusLoadPromise = new Promise((resolve, reject) => {
    console.log("📥 Loading Zaius script...");

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;

    script.onload = function () {
      console.log("✅ Zaius script loaded successfully");
      resolve();
    };

    script.onerror = function () {
      console.error("🔥 Failed to load Zaius script");
      zaiusLoadPromise = null; // Reset so it can be retried
      reject(new Error("Failed to load Zaius script"));
    };

    // Use environment-specific Zaius script to prevent user merging
    const hostname = window.location.hostname;
    const isTest = hostname.includes("test") || hostname.startsWith("dev") || hostname.startsWith("local") || hostname.includes("predeploy");
    const zaiusId = isTest ? 'lKIe3kN5LlPetJ29HYbXVg-au1' : 'wbJmydsdMnYBbOTXjub8BA-au1';
    script.src = `https://d1igp3oop3iho5.cloudfront.net/v2/${zaiusId}/zaius-min.js`;

    document.head.appendChild(script);
  });

  return zaiusLoadPromise;
}


async function startTokenOrSignin(redirectPage = null) {
  const config = getConfig(window.location);
  const oidcUserManager = new UserManager(config);

  // check if user is logged in else trigger login
  const token = await getAccessTokenOrSignin(oidcUserManager, redirectPage);
  console.log('getAccessTokenOrSignin may call oidcUserManager.signinRedirect() ');
  return { oidcUserManager, token };
}


// Gets a valid access token if the user is still logged in,
// otherwise kicks off login (with some tracking + debounce).
async function getAccessTokenOrSignin(oidcUserManager, redirectPage = null) {

  const user = await oidcUserManager.getUser();

  localStorage.setItem('getAccessTokenOrSignin', "true");
  const lastLogin = sessionStorage.getItem('lastLogin');
  const now = new Date().getTime();

  // Use the new coordinated ZAIUS loading approach
  try {
    // Check if ZAIUS is already loaded and initialized
    if (window.zaius && typeof window.zaius !== "undefined" && typeof window.zaius.push === "function") {
      localStorage.setItem("isZaiusInitiated", zaiusInitialized);
      executeZaiusActions(user);
    } else {
      // Ensure ZAIUS is loaded before proceeding
      await ensureZaiusLoaded();

      // After loading, execute actions
      localStorage.setItem("isZaiusInitiated", false);
      executeZaiusActions(user);
    }
  } catch (error) {
    console.error("🔥 Error loading or initializing ZAIUS:", error);
    // Continue with the rest of the function even if ZAIUS fails
  }

  // Fix: Use consistent timestamp format - user.expires_at is already in seconds
  const nowInSeconds = Math.floor(now / 1000);


  if (!!user && nowInSeconds < user.expires_at) {  // Fixed: consistent seconds comparison
    // console.log('typeof user.expires_at: ', typeof user.expires_at);
    console.log('expires at: ', user.expires_at);
    console.log('not expired?: ', nowInSeconds < user.expires_at);
    console.log('remaining time (seconds): ', (user.expires_at - nowInSeconds));

    // Update localStorage with consistent format
    localStorage.setItem('user_expires_at', user.expires_at);
    localStorage.setItem('LOGGED_IN', true);

    return 'Bearer ' + user.access_token;
  }

  // Redirect technique
  sessionStorage.removeItem('tripModal');

  setRedirectPage(redirectPage);

  const waitTime = calcWaitTime(lastLogin);

  if (!lastLogin || now > waitTime) {  // greater than debounceTime
    sessionStorage.setItem('lastLogin', now);
    await oidcUserManager.signinRedirect();
  } else {
    // grey out AddToTripModal button until timeout has passed
    disableButtonsTemporarily(lastLogin);
  }

  return null;
}

function getPRID() {
  const profileValues = buildAfxProfileValues();
  localStorage.setItem("getPRID", profileValues.prid);
  if (profileValues?.prid) {
    const hcSatcId = profileValues.prid;
    return hcSatcId; // Return as an object
  } else {
    localStorage.setItem("prid is not available.", "noPRID");
    return null;
  }
}

function buildAfxProfileValues() {
  // Get the `afx_profile` cookie value
  const cookieValue = document.cookie
    .split('; ')
    .find(row => row.startsWith('afx_profile='))
    ?.split('=')[1];

  if (!cookieValue) {
    return { acid: null, prid: null };
  }
  // Decode the cookie JSON string and parse it
  const afxProfile = JSON.parse(decodeURIComponent(cookieValue));
  // Extract the acid and prid values
  const acid = afxProfile.acid || null;
  const prid = afxProfile.prid || null;
  return { acid, prid };
}


function setRedirectPage(redirectPage = null) {
  // remember current page for future redirects to return. But NOT /signin-oidc pg.
  const currentPage = redirectPage || `${window.location.href}`;
  // if current page is signin-oidc then don't set it to sessionStorage.
  currentPage.includes('signin-oidc') ? null : sessionStorage.setItem('redirectPage', currentPage);
}


async function getUser(oidcUserManager) {
  // should work even if not supplied a oidcUserManager
  if (!oidcUserManager) {
    console.log('no oidcUserManager. Creating one.');
    const config = getConfig(window.location);
    oidcUserManager = new UserManager(config);
  }

  const user = await oidcUserManager.getUser();

  if (!!user) {
    localStorage.setItem('user_expires_at', user.expires_at);
  }

  // return new Promise((resolve, reject) => {
  //   console.log('getUser debounce.');
  //   let count = 0;

  //   const interval = setInterval(() => {
  //     if(user) {
  //       clearInterval(interval);
  //       resolve(user.profile);
  //     }

  //     count++;
  //     if(count > 10) {
  //       clearInterval(interval);
  //       resolve('');
  //     }
  //   }, 200);
  // });
  return user;
}


function calcWaitTime(lastLogin) {
  // calculates wait remaining from debounceTime for use in a setTimeout.
  if (!!lastLogin) {
    // if last login.
    const now = new Date().getTime();  // returns time in milliseconds

    if ((parseInt(lastLogin) + debounceTime) > now) {
      // if debounceTime is greater than now.
      return (parseInt(lastLogin) + debounceTime) - now;
    }
  }

  return 0; // if no lastLogin or lastLogin too long ago.
}


async function disableButtonsTemporarily(lastLogin) {
  const wait = calcWaitTime(lastLogin);
  $('.tripplan-button').addClass('disabled');
  $('.add-to-trip__button').addClass('disabled');
  setTimeout(function () {
    $('.tripplan-button').removeClass('disabled');
    $('.add-to-trip__button').removeClass('disabled');
  }, wait);
}


async function logoutRemote() {
  try {

    // Send a logout message to trip-southaustralia.com
    let message = { 'code': 'logout_user' }
    let iframe = document.querySelector("#trip-frame");
    iframe.contentWindow.postMessage(message, "*");

    // Logout from our own instance to sign the logout action
    createSignoutUrlOpenInIframe();
    // const config = getConfig(window.location);
    // let oidcUserManager = new UserManager(config);
    // await oidcUserManager.signoutRedirect();
    // // // After the signoutRedirect, you can redirect to a new page.
    // window.location.href = "https://" + window.location.host;
  } catch (error) {
    console.error('Error during signout:', error);
  }
}


function logoutLocal() {
  localStorage.removeItem('LOGGED_IN');
  sessionStorage.removeItem('lastLogin');
  sessionStorage.removeItem('signinPageVisited');
  let config = getConfig(window.location);
  sessionStorage.removeItem(`oidc.user:${config.authority}:${config.client_id}`);
  localStorage.removeItem(`user_token`);
  localStorage.removeItem(`user_expires_at`);
  // on logout, remove certain hashes from URL
  removeStaleHash();
  console.log('Removed stale hash.');
}


function removeStaleHash() {
  const disallowed = ['#', '#favourites', '#profile', '#settings'];

  if (disallowed.includes(window.location.hash)) {
    history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search
    );
  }
}


async function createSignoutUrlOpenInIframe() {
  // console.log('createSignoutUrl');
  const config = getConfig(window.location);
  let oidcUserManager = new UserManager(config);
  // console.log('oidcUserManager', oidcUserManager);
  let user = await oidcUserManager.getUser();

  if (user != null) {
    let id_token_hint = user.id_token;
    let data = '';
    let state = user.session_state;
    const hostname = location.hostname;
    // Despite setting a post_logout_redirect_uri it appears to be ignored by the idserver.
    let post_logout_redirect_uri = `${location.protocol}//${hostname}${location.port ? `:${location.port}` : ''}/signout-oidc`;
    let extraQueryParams = {};
    let request_type = 'Bearer';
    // , extraQueryParams, request_type
    let signoutRequestProperties = { id_token_hint, data, state, post_logout_redirect_uri, extraQueryParams, request_type };
    oidcUserManager.createSignoutRequest(signoutRequestProperties)
      .then(signout_request => {
        console.log('signout_request.url', signout_request.url);
        let iframeFragment = document.createElement('iframe');
        iframeFragment.src = signout_request.url;
        iframeFragment.id = 'signout';
        iframeFragment.style.display = 'none';
        document.querySelector('main').appendChild(iframeFragment);
        // fetch(signout_request.url)  // Fetch get's redirected and then throws an error. Did it still achieve the remote signout?
        // .then(response => {
        //   console.log('TEST 2');
        //   resolve(response)
        // })
        // .catch(err => {
        //   console.error('Error during signout:', err);
        //   reject(err);
        // })
      });
  } else {
    console.log('no user, already signed out.');
  }


  // } catch (error) {
  //     console.error('Error during signout:', error);
  // }
}


function truncateString(str, num) {
  if (typeof str !== 'string' || str.length <= num) {
    return str
  }
  return str.slice(0, num);
}


function checkUserToken() {
  const expires = localStorage.getItem('user_expires_at') || null;
  let token = null;
  const nowInSeconds = Math.floor(Date.now() / 1000); // Use consistent seconds format

  console.log('expires_at: ', expires);
  console.log('nowInSeconds: ', nowInSeconds);

  if (!!expires && parseInt(expires) > nowInSeconds) {
    token = localStorage.getItem('user_token') || null;
    console.log('TOKEN NOT EXPIRED.');

    // Sync authentication state
    if (token) {
      localStorage.setItem('LOGGED_IN', true);
    }
  } else if (expires) {
    console.log('TOKEN EXPIRED. Cleaning up authentication state.');
    // Clean up expired authentication state
    localStorage.removeItem('LOGGED_IN');
    localStorage.removeItem('user_token');
    localStorage.removeItem('user_expires_at');
  }
  return token;
}

async function simpleLoginCheck() {
  // silent login check, returns user or null
  // not reliable check as token may be expired.
  const config = getConfig(window.location);
  let oidcUserManager = new UserManager(config);

  let user = null;

  user = await getUser(oidcUserManager);

  return user;
}

async function checkLoginGetStatus() {
  // checks login status and returns an object containing user and token.

  const config = getConfig(window.location);
  const oidcUserManager = new UserManager(config);
  // console.log('config: ', config);
  // console.log('oidcUserManager: ', oidcUserManager);

  // First check if we have a valid token in localStorage
  let token = checkUserToken();
  let user = null;

  if (token) {
    // We have a valid token, try to get user info
    user = await getUser(oidcUserManager);

    // If we have token but no user, the token might be invalid
    if (!user) {
      console.log('Valid token but no user - clearing token state');
      localStorage.removeItem('user_token');
      localStorage.removeItem('user_expires_at');
      localStorage.removeItem('LOGGED_IN');
      token = null;
    }
  } else {
    // No valid token, try to get fresh user/token from OIDC
    user = await getUser(oidcUserManager);

    if (!!user) {
      // const token = await this.getAccessToken(oidcUserManager);
      token = await getAccessTokenOrSignin(oidcUserManager);
      // console.log('AA token: ', token);
      if (token) {
        localStorage.setItem('user_token', token);
      }
    }
  }

  // Final authentication state sync
  if (token && user) {
    localStorage.setItem('LOGGED_IN', true);
  } else {
    localStorage.removeItem('LOGGED_IN');
  }

  return { user, token };// no user or no token. Therefore not logged in.

}


// Helper function to set authentication state from external sources (like URL params)
function setExternalAuthState(userToken, userExpiresAt, loggedIn = true) {
  if (userToken && userExpiresAt) {
    const nowInSeconds = Math.floor(Date.now() / 1000);
    const expiresAtSeconds = parseInt(userExpiresAt);

    if (expiresAtSeconds > nowInSeconds) {
      localStorage.setItem('user_token', userToken.startsWith('Bearer ') ? userToken : `Bearer ${userToken}`);
      localStorage.setItem('user_expires_at', expiresAtSeconds);
      localStorage.setItem('LOGGED_IN', loggedIn.toString());
      console.log('External authentication state set successfully');
      return true;
    } else {
      console.warn('External token is expired, not setting authentication state');
      return false;
    }
  }
  console.warn('Invalid external authentication parameters');
  return false;
}

// signIn as default,
export { checkUserToken, checkLoginGetStatus, getConfig, startTokenOrSignin, getAccessTokenOrSignin, getUser, UserManager, logoutRemote, logoutLocal, disableButtonsTemporarily, calcWaitTime, setRedirectPage, setExternalAuthState, ensureZaiusLoaded, simpleLoginCheck };
